import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { enumPolicies } from '../../../_metronic/helpers/enumPolicies';
import PolicyChecker from '../../../_metronic/helpers/components/PolicyChecker';
import OrderDetailFields from '../../components/OrderDetailFields';

const ProductionsList = ({
    displayData,
    loading,
    expandedOrder,
    setExpandedOrder,
    onContentCheckChange,
    onPrintContent,
    intl,
    readOnlyCheckboxes = false
}) => {

    const renderContentImageLink = (content) => {
        const imageSrc = content?.imageLink ? content.imageLink : '/media/stock/ecommerce/default-product.png';
        return (
            <div className="symbol symbol-circle symbol-50px me-3">
                <div className="symbol-label">
                    <img src={imageSrc} alt={content.productName} className="w-100" />
                </div>
            </div>
        )
    }

    const renderContentItemName = (content) => {
        if (content && content.productId > 0) {
            return (
                <div className="d-flex align-items-center">
                    <a target='_blank' href={`/catalog-management/products/${content.productId}/detail`}>
                        {renderContentImageLink(content)}
                    </a>
                    <div className="d-flex flex-column">
                        <a target='_blank' href={`/catalog-management/products/${content.productId}/detail`} className="text-dark text-hover-primary mb-1 fw-bold">{content.productName} - <span className='fw-light'>{content.productCode}</span></a>
                        <span className='fw-bold'>{content.variantName} - <span className='fw-light'>{content.variantCode}</span></span>
                    </div>
                </div>
            )
        }
        return (
            <>
                {content.name}
            </>
        )
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center p-10">
                <span className="spinner-border text-primary" role="status"></span>
                <span className="text-gray-800 fs-6 fw-semibold ms-3">{intl.formatMessage({ id: 'COMMON.LOADING' })}...</span>
            </div>
        );
    }

    if (!displayData || displayData.length === 0) {
        return (
            <div className="text-center">
                {intl.formatMessage({ id: 'PRODUCTIONS.FIELD.NO_DATA' })}
            </div>
        );
    }

    return (
        <div className="accordion" id="productionsAccordion">
            {displayData.map((order, index) => {
                const finishedItems = order.orderContents ? order.orderContents.filter(c => c.checked).length : 0;
                const totalItemsInOrder = order.orderContents ? order.orderContents.length : 0;
                const isExpanded = expandedOrder === order.orderId;

                return (
                    <div className={`accordion-item ${isExpanded ? 'bg-light' : ''}`} key={`order_${order.orderId}`}>
                        <h2 className="accordion-header" id={`heading_${order.orderId}`}>
                            <button
                                className={`accordion-button ${isExpanded ? '' : 'collapsed'}`}
                                type="button"
                                onClick={() => setExpandedOrder(isExpanded ? null : order.orderId)}
                            >
                                <div className='row w-100 pe-4 align-items-center'>
                                    <div className='col-12 col-md-6 mb-2 mb-md-0 d-flex align-items-center'>
                                        {order.orderReady ? (
                                            <i className="ki-duotone ki-check-circle fs-2 text-success me-2">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                        ) : (
                                            <i className="ki-duotone ki-time fs-2 text-warning me-2">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                        )}
                                        <div className='d-flex flex-column'>
                                            <div>
                                                <strong>{intl.formatMessage({ id: 'PRODUCTIONS.FIELD.ORDER' })}:</strong> <Link to={`/order-management/orders/${order.orderId}`} onClick={(e) => e.stopPropagation()}>#{order.orderId}</Link>
                                            </div>
                                            {order.orderCompanyName && <span className='fw-bold'>{order.orderCompanyName}</span>}
                                            <span className='fw-light'>{order.orderReference}</span>
                                        </div>
                                    </div>
                                    <div className='col-6 col-md-3'>
                                        <strong>{intl.formatMessage({ id: 'PRODUCTIONS.FIELD.ITEMS' })}:</strong> {finishedItems} / {totalItemsInOrder}
                                    </div>
                                    <div className='col-6 col-md-3 text-end'>
                                        <strong>{intl.formatMessage({ id: 'PRODUCTIONS.FIELD.PRODUCTION_DATE' })}:</strong> {moment(order.productionDate).format('DD-MM-YYYY')}
                                    </div>
                                </div>
                            </button>
                        </h2>
                        <div
                            id={`collapse_${order.orderId}`}
                            className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                            aria-labelledby={`heading_${order.orderId}`}
                            data-bs-parent="#productionsAccordion"
                        >
                            <div className="accordion-body">
                                <div className='table-responsive'>
                                    <table className="table align-middle table-row-dashed table-row-gray-500 fs-7 gy-2">
                                        <thead>
                                            <tr className="fw-bolder fs-7 text-dark">
                                                <th>{intl.formatMessage({ id: "ORDER.FIELD.PRODUCTNAME" })}</th>
                                                <th className='text-center'>{intl.formatMessage({ id: "ORDER.FIELD.QUANTITY" })}</th>
                                                <th>{intl.formatMessage({ id: "ORDER.FIELD.DETAIL" })}</th>
                                                <PolicyChecker policies={[enumPolicies.OrderAdmin, enumPolicies.OrderPersonnel]}>
                                                    <th className='text-center'>{intl.formatMessage({ id: "ORDER.FIELD.READY" })}</th>
                                                </PolicyChecker>
                                                <th className='text-end min-w-100px'>{intl.formatMessage({ id: 'PRODUCTIONS.FIELD.ACTIONS' })}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-dark">
                                            {order.orderContents && order.orderContents.map((content, contentIndex) => {
                                                var detail = content.additionalAttributesJson != '' && JSON.parse(content.additionalAttributesJson);
                                                return (
                                                    <tr key={`production_content_${content.id}`} className={content.checked ? 'bg-light-success' : ''}>
                                                        <td>{renderContentItemName(content)}</td>
                                                        <td className="text-center">{content.quantity}</td>
                                                        <td style={{ minWidth: '200px' }}>
                                                            <OrderDetailFields detail={detail} intl={intl} />
                                                        </td>
                                                        <PolicyChecker policies={[enumPolicies.OrderAdmin, enumPolicies.OrderPersonnel]}>
                                                            <td className='text-center'>
                                                                <div className="form-check form-check-custom form-check-solid d-inline-block">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={content.checked}
                                                                        onChange={() => !readOnlyCheckboxes && onContentCheckChange(content)}
                                                                        disabled={readOnlyCheckboxes}
                                                                        style={{ transform: 'scale(1.5)', cursor: readOnlyCheckboxes ? 'default' : 'pointer' }}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </PolicyChecker>
                                                        <td className='text-end pe-4 rounded-end'>
                                                            <button type="button" className="btn btn-sm btn-icon btn-light" onClick={() => onPrintContent(content)}>
                                                                <i className="ki-duotone ki-printer fs-3">
                                                                    <span className="path1"></span>
                                                                    <span className="path2"></span>
                                                                    <span className="path3"></span>
                                                                    <span className="path4"></span>
                                                                    <span className="path5"></span>
                                                                </i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    );
};

export default ProductionsList;
